import { Component, ContentChild, Input, TemplateRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { CustomCurrencyPipe } from '@shared/pipe/custom-currency/custom-currency.pipe';
import { COLORS, PALETTE_PRIMARY } from 'taxtank-core';
import { BaseComponent } from '@shared/component/base/base.component';

/**
 * @TODO refactor charts system with error handling and beautiful chart data
 */
@Component({
  selector: 'app-base-chart',
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BaseChartComponent extends BaseComponent {
  @ContentChild('tooltip') tooltipTemplate?: TemplateRef<{ $implicit: unknown; tooltip: unknown }>;
  // chart data source
  @Input() data: any[] = [];
  // id of html container to display chart
  @Input() containerId?: string;
  // field for data
  @Input() bindSeriesData = 'data';
  // field for data name
  @Input() bindSeriesName = 'name';
  // field name for column value
  @Input() bindY = 'value';
  // field name for xAxis label
  @Input() bindX = 'label';
  // flag define how to display xAxis with date or with category
  @Input() isDateChart: boolean;
  // flag define how to display yAxis: as currency or not
  @Input() isCurrency = true;
  @Input() currency = 'AUD';
  @Input() showLegend = false;
  @Input() palette = PALETTE_PRIMARY;
  @Input() xAxisLineHidden = false;
  // chart settings
  chartOptions: any;
  // data based on @Input data to display on chart
  chartData: any;
  public customCurrencyPipe = inject(CustomCurrencyPipe);

  setDefaultOptions(customCurrencyPipe: CustomCurrencyPipe): void {
    this.chartOptions = {
      // Disable HighCharts label on chart
      credits: {
        enabled: false
      },
      // X axis settings
      xAxis: {
        dateTimeLabelFormats: {
          // show short month label
          month: '%b'
        },
        labels: {
          style: {
            color: COLORS.grey,
            fontWeight: 'bold',
            fontSize: '12px',
            fontFamily: 'Montserrat, sans-serif',
            textTransform: 'uppercase'
          }
        },
        // disable short vertical line from xAxis line to xAsix label
        tickLength: 0
      },
      yAxis: {
        title: null,
        labels: {
          formatter: function (): string {
            return customCurrencyPipe.transform(this.value, { decimals: 0 });
          },
          style: {
            color: COLORS.grey,
            fontWeight: 'bold',
            fontSize: '12px',
            fontFamily: 'Montserrat, sans-serif',
            textTransform: 'uppercase'
          }
        }
      },
      // common chart settings
      chart: {
        backgroundColor: 'transparent'
      },
      plotOptions: {
        areaspline: {
          marker: {
            fillColor: COLORS.white,
            lineWidth: 2,
            lineColor: null,
            symbol: 'circle'
          },
        },
        pie: {
          dataLabels: {
            enabled: true,
            format: '{point.percentage:.1f}%'
          }
        },
        series: {
          pointPadding: 0.05,
          borderWidth: 0
        }
      },
      tooltip: {
        backgroundColor: COLORS.white,
        borderWidth: 1,
        borderRadius: 4,
        shadow: false,
        useHTML: true,
        style: {
          padding: 15
        }
      },
      title: null,
      legend: {
        enabled: this.showLegend,
        align: 'right',
        verticalAlign: 'top',
      },
      colors: this.palette
    };

    this.hideXAxisLine();
  }

  hideXAxisLine(): void {
    if (!this.xAxisLineHidden) {
      return;
    }

    this.chartOptions.xAxis.lineWidth = 0;
    // Some charts draw the zero baseline through yAxis.plotLines, which visually acts as the x-axis line.
    this.chartOptions.yAxis.plotLines = [];
  }

  /**
   * Keeps tooltip setup in one place because Highcharts calls formatter outside Angular context,
   * while our charts may provide either a projected Angular template or a plain Highcharts formatter.
   */
  protected setTooltipFormatter(defaultFormatter: (context: any) => string): void {
    const self = this;

    this.chartOptions.tooltip.formatter = function (): string {
      // Use the projected Angular tooltip template when provided; otherwise fall back to the chart default formatter.
      if (self.tooltipTemplate) {
        return self.renderTooltipTemplate(this.point.tooltip);
      }

      return defaultFormatter(this);
    };
  }

  /**
   * Converts the projected Angular tooltip template into the HTML string expected by Highcharts.
   */
  renderTooltipTemplate(context: unknown): string {
    if (!this.tooltipTemplate) {
      return '';
    }

    const viewRef = this.tooltipTemplate.createEmbeddedView({
      $implicit: context,
      tooltip: context
    });

    viewRef.detectChanges();
    const html = viewRef.rootNodes[0].outerHTML;
    viewRef.destroy();

    return html;
  }
}
